import { formatCurrency } from '../utils/dateutils';
import { formatKeyForDisplay } from '../utils/commonutils';

export const V2_IGNORED_NODE_KEYS = new Set(['stop_gates', 'error_message', 'meta', 'order', 'version', 'sources']);

export const V2_REQUESTS_SECTION_KEY = 'requests';

/** Vertical stack for fields directly inside accordion content (no left indent). */
export const V2_ACCORDION_CONTENT_CLASS = 'space-y-1';

/** Left indent + stack for nested subsections only (e.g. Details, fields under a section title). */
export const V2_NESTED_SECTION_CLASS = 'space-y-1 pl-2';

export type V2SourceId = {
    source_id: string;
    file_name?: string;
    pages?: number[];
};

export const toV2SourceIds = (ids: unknown): V2SourceId[] | null => {
    if (!Array.isArray(ids) || ids.length === 0) return null;
    const valid = ids.filter(
        (item): item is V2SourceId =>
            item != null &&
            typeof item === 'object' &&
            typeof (item as V2SourceId).source_id === 'string'
    );
    return valid.length > 0 ? valid : null;
};

export type ActionOutputV2Sources = {
    source_ids?: V2SourceId[];
    pdf_source_ids?: V2SourceId[];
    [key: string]: unknown; // includes @template fields
};

export type ActionOutputV2Payload = {
    score?: number;
    explanation?: Record<string, unknown>;
    order?: Array<string | number>;
    version?: number | string;
    meta?: { version?: number | string; [key: string]: unknown };
    sources?: ActionOutputV2Sources;
    stop_gates?: unknown;
};

export type NumericKeySlot = {
    key: string;
    label: string;
    index: number;
};

export type BoxAccordionIcon = {
    icon: string;
    iconColor: string;
    bgColor: string;
    subtitle?: string;
};

/** Matches ClaimScoreGeneratorRenderer actionMetadata for known request actions. */
const V2_KNOWN_ACTION_BOX: Record<string, BoxAccordionIcon> = {
    repair_profile: {
        icon: 'mdi:wrench',
        iconColor: 'text-purple-600',
        bgColor: 'bg-purple-100',
        subtitle: 'Cost and frequency analysis',
    },
    '3Cs_text_analysis': {
        icon: 'mdi:clipboard-text',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-100',
        subtitle: 'Complaint, Cause, Correction',
    },
    "3C's_analysis": {
        icon: 'mdi:clipboard-text',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-100',
        subtitle: 'Complaint, Cause, Correction',
    },
    coverage_validation: {
        icon: 'mdi:shield-check',
        iconColor: 'text-green-600',
        bgColor: 'bg-green-100',
        subtitle: 'Policy and coverage status',
    },
    cost_validation: {
        icon: 'mdi:alert-circle',
        iconColor: 'text-orange-600',
        bgColor: 'bg-orange-100',
        subtitle: 'Cost validation',
    },
    video_analysis: {
        icon: 'mdi:video-outline',
        iconColor: 'text-violet-600',
        bgColor: 'bg-violet-100',
        subtitle: 'Video review and scoring',
    },
    image_analysis: {
        icon: 'mdi:image-outline',
        iconColor: 'text-pink-600',
        bgColor: 'bg-pink-100',
        subtitle: 'Image review and scoring',
    },
};

const boxAccordionIcon = (icon: string, iconColor: string, bgColor: string): BoxAccordionIcon => ({
    icon,
    iconColor,
    bgColor,
});

/** Keyword fragment → icon; first match wins (order matters). */
const V2_KEYWORD_BOX_ICON_RULES: ReadonlyArray<{ fragment: string; icon: BoxAccordionIcon }> = [
    { fragment: 'video', icon: V2_KNOWN_ACTION_BOX.video_analysis },
    { fragment: 'image', icon: V2_KNOWN_ACTION_BOX.image_analysis },
    { fragment: 'odometer', icon: boxAccordionIcon('mdi:speedometer', 'text-blue-600', 'bg-blue-100') },
    { fragment: 'owner', icon: boxAccordionIcon('mdi:account-check', 'text-indigo-600', 'bg-indigo-100') },
    { fragment: 'customer', icon: boxAccordionIcon('mdi:account', 'text-sky-600', 'bg-sky-100') },
    { fragment: 'damage', icon: boxAccordionIcon('mdi:car-emergency', 'text-orange-600', 'bg-orange-100') },
    { fragment: 'repair', icon: boxAccordionIcon('mdi:wrench', 'text-purple-600', 'bg-purple-100') },
    { fragment: 'vehicle', icon: boxAccordionIcon('mdi:car-side', 'text-red-600', 'bg-red-100') },
    { fragment: 'analysis', icon: boxAccordionIcon('mdi:chart-line', 'text-teal-600', 'bg-teal-100') },
    { fragment: 'date', icon: boxAccordionIcon('mdi:calendar', 'text-default-500', 'bg-default-100') },
];

const BOX_ICON_POOL_FALLBACK: BoxAccordionIcon[] = [
    { icon: 'mdi:file-document-check', iconColor: 'text-indigo-600', bgColor: 'bg-indigo-100' },
    { icon: 'mdi:chart-line', iconColor: 'text-teal-600', bgColor: 'bg-teal-100' },
    { icon: 'mdi:cog', iconColor: 'text-default-500', bgColor: 'bg-default-100' },
    { icon: 'mdi:information', iconColor: 'text-sky-600', bgColor: 'bg-sky-100' },
    { icon: 'mdi:clipboard-list', iconColor: 'text-amber-600', bgColor: 'bg-amber-100' },
    { icon: 'mdi:check-decagram', iconColor: 'text-cyan-600', bgColor: 'bg-cyan-100' },
];

const hashString = (str: string): number => {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
        h = (h << 5) - h + (str.codePointAt(i) ?? 0);
        h = Math.trunc(h);
    }
    return Math.abs(h);
};

const parseVersionNumber = (version: unknown): number | null => {
    if (version == null) return null;
    if (typeof version === 'number' && Number.isFinite(version)) return version;
    if (typeof version === 'string') {
        const n = Number.parseFloat(version);
        return Number.isFinite(n) ? n : null;
    }
    return null;
};

/** V2 `action_output` is the scored tree at the root (not nested under agent name). */
export const isActionOutputV2 = (data: unknown): boolean => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
    const obj = data as Record<string, unknown>;
    const meta = obj.meta as { version?: unknown } | undefined;
    const v = parseVersionNumber(obj.version) ?? parseVersionNumber(meta?.version);
    if (v === null || v < 2) return false;
    const exp = obj.explanation;
    return exp != null && typeof exp === 'object' && !Array.isArray(exp);
};

/** Old v2 shape: payload under an agent name, often as a one-element array. */
const extractV2FromAgentBucket = (bucket: unknown): ActionOutputV2Payload | null => {
    if (bucket == null) return null;
    const candidate = Array.isArray(bucket) ? bucket[0] : bucket;
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
    return isActionOutputV2(candidate) ? (candidate as ActionOutputV2Payload) : null;
};

function nestedDifyActionOutput(root: Record<string, unknown>): unknown {
    if (root.action_output !== undefined) return root.action_output;
    if (root.action_outpput !== undefined) return root.action_outpput;
    const data = root.data;
    if (data != null && typeof data === 'object' && !Array.isArray(data)) {
        const inner = data as Record<string, unknown>;
        if (inner.action_output !== undefined) return inner.action_output;
        const outputs = inner.outputs;
        if (outputs != null && typeof outputs === 'object' && !Array.isArray(outputs)) {
            const out = outputs as Record<string, unknown>;
            return out.action_output ?? out.action_outpput ?? outputs;
        }
        if (inner.outputs !== undefined) return inner.outputs;
    }
    return root.outputs;
}

/**
 * Same V2 tree the renderer already understands. Dify may wrap it in extra
 * `action_output` / `data.outputs` layers — walk those until `version` + `explanation`.
 */
export const getActionOutputV2Payload = (actionOutput: unknown, depth = 0): ActionOutputV2Payload | null => {
    if (actionOutput == null || depth > 12) return null;
    if (Array.isArray(actionOutput)) {
        return getActionOutputV2Payload(actionOutput[0], depth + 1);
    }
    if (typeof actionOutput !== 'object') return null;
    const root = actionOutput as Record<string, unknown>;
    if (isActionOutputV2(root)) return root;

    const nested = nestedDifyActionOutput(root);
    if (nested !== undefined && nested !== actionOutput) {
        const found = getActionOutputV2Payload(nested, depth + 1);
        if (found) return found;
    }

    for (const bucket of Object.values(root)) {
        const fromBucket = extractV2FromAgentBucket(bucket);
        if (fromBucket) return fromBucket;
    }
    return null;
};

/** Claim object from V2 `sources` (e.g. `input_claim_object@template`) for UXGenie / Sources tab. */
export const getV2SourcesTemplateData = (
    sources: ActionOutputV2Sources | undefined
): Record<string, unknown> | null => {
    if (!sources || typeof sources !== 'object') return null;
    const templateKey = Object.keys(sources).find((k) => k.endsWith('@template'));
    const val = templateKey ? sources[templateKey] : null;
    return val as Record<string, unknown> | null;
};

/** Knowledge/PDF source chips from V2 `sources.source_ids` and `sources.pdf_source_ids`. */
export const getV2SourcesKnowledgeIds = (
    sources: ActionOutputV2Sources | undefined
): V2SourceId[] | null => {
    if (!sources || typeof sources !== 'object') return null;
    const all = [
        ...(toV2SourceIds(sources.source_ids) ?? []),
        ...(toV2SourceIds(sources.pdf_source_ids) ?? []),
    ];
    if (all.length === 0) return null;
    const seen = new Set<string>();
    const merged: V2SourceId[] = [];
    for (const item of all) {
        if (seen.has(item.source_id)) continue;
        seen.add(item.source_id);
        merged.push(item);
    }
    return merged.length > 0 ? merged : null;
};

/** V2-only: knowledge source IDs from `action_output` (does not affect legacy extraction). */
export const getV2ActionOutputKnowledgeSourceIds = (
    actionOutput: unknown
): V2SourceId[] | null => {
    const payload = getActionOutputV2Payload(actionOutput);
    return getV2SourcesKnowledgeIds(payload?.sources);
};

const UUID_EXPLANATION_KEY_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Numeric slot keys (`1`, `2`) and attachment `source_id` UUIDs under explanation objects. */
export const isNumericExplanationKey = (key: string | number): boolean => {
    if (typeof key === 'number') return Number.isFinite(key);
    const s = String(key);
    return /^\d+$/.test(s) || UUID_EXPLANATION_KEY_RE.test(s);
};

export type V2ScoredNode = {
    score?: number;
    explanation?: Record<string, unknown>;
    order?: Array<string | number>;
};

export const isScoredNode = (value: unknown): value is V2ScoredNode => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const exp = (value as { explanation?: unknown }).explanation;
    return exp != null && typeof exp === 'object' && !Array.isArray(exp);
};

export const resolveOrderedKeys = (
    explanation: Record<string, unknown> | null | undefined,
    order?: Array<string | number> | null
): string[] => {
    if (!explanation || typeof explanation !== 'object') return [];
    const keys = Object.keys(explanation);
    if (!order?.length) return keys;
    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const k of order) {
        const key = String(k);
        // Exact match first; fall back to matching on base name (ignoring @suffix like @date, @miles).
        const match = keys.includes(key) ? key : keys.find((ek) => ek.startsWith(`${key}@`) && !seen.has(ek));
        if (match && !seen.has(match)) {
            ordered.push(match);
            seen.add(match);
        }
    }
    for (const k of keys) {
        if (!seen.has(k)) ordered.push(k);
    }
    return ordered;
};

export const expandNumericKeySlots = (
    explanation: Record<string, unknown>,
    order?: Array<string | number> | null,
    parentSectionKey?: string
): NumericKeySlot[] => {
    const numericKeys = Object.keys(explanation).filter(isNumericExplanationKey);
    if (numericKeys.length === 0) return [];

    const baseLabel = parentSectionKey ? formatSectionTitle(parentSectionKey) : 'Item';
    const slots: NumericKeySlot[] = [];

    if (order?.length) {
        let slotIndex = 0;
        for (const orderKey of order) {
            const key = String(orderKey);
            if (!isNumericExplanationKey(key) || !(key in explanation)) continue;
            slotIndex += 1;
            slots.push({
                key,
                label: `${baseLabel} ${slotIndex}`,
                index: slotIndex,
            });
        }
        return slots;
    }

    return numericKeys.map((key, idx) => ({
        key,
        label: `${baseLabel} ${idx + 1}`,
        index: idx + 1,
    }));
};

export const hasOnlyNumericExplanationKeys = (explanation: Record<string, unknown>): boolean => {
    const keys = Object.keys(explanation);
    return keys.length > 0 && keys.every(isNumericExplanationKey);
};

/** Top-level accordion section: scored node, plain object, or array. */
export type V2TopLevelSectionValue = V2ScoredNode | Record<string, unknown> | unknown[];

export type V2TopLevelAccordionEntry = {
    itemKey: string;
    title: string;
    sectionKey: string;
    sectionValue: V2TopLevelSectionValue;
};

export type V2TopLevelOrderedEntry =
    | { kind: 'primitive'; key: string; value: unknown }
    | {
          kind: 'section';
          itemKey: string;
          title: string;
          sectionKey: string;
          sectionValue: V2TopLevelSectionValue;
      };

/** Top-level fields in `order` sequence — primitives and sections interleaved. */
export const buildV2TopLevelOrderedEntries = (
    topExplanation: Record<string, unknown>,
    topOrder?: Array<string | number> | null
): V2TopLevelOrderedEntry[] => {
    const entries: V2TopLevelOrderedEntry[] = [];
    forEachV2TopLevelField(topExplanation, topOrder, (key, value) => {
        if (isV2NonPrimitiveValue(value)) {
            entries.push({
                kind: 'section',
                itemKey: `v2-top-${key}`,
                title: formatSectionTitle(key),
                sectionKey: key,
                sectionValue: value as V2TopLevelSectionValue,
            });
        } else {
            entries.push({ kind: 'primitive', key, value });
        }
    });
    return entries;
};

/**
 * Top-level line accordion entries: scored nodes, plain objects, and arrays (not primitives).
 */
export const buildV2TopLevelAccordionEntries = (
    topExplanation: Record<string, unknown>,
    topOrder?: Array<string | number> | null
): V2TopLevelAccordionEntry[] =>
    buildV2TopLevelOrderedEntries(topExplanation, topOrder)
        .filter((entry): entry is Extract<V2TopLevelOrderedEntry, { kind: 'section' }> => entry.kind === 'section')
        .map(({ itemKey, title, sectionKey, sectionValue }) => ({
            itemKey,
            title,
            sectionKey,
            sectionValue,
        }));

/** Leaf scalars used for mixed vs all-boxed layout decisions. */
export const isV2PrimitiveValue = (value: unknown): boolean =>
    value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

/** Objects and arrays (non-null). */
export const isV2NonPrimitiveValue = (value: unknown): boolean => !isV2PrimitiveValue(value);

const forEachV2TopLevelField = (
    topExplanation: Record<string, unknown>,
    topOrder: Array<string | number> | null | undefined,
    visit: (key: string, value: unknown) => void
): void => {
    for (const key of resolveOrderedKeys(topExplanation, topOrder)) {
        if (V2_IGNORED_NODE_KEYS.has(key) || isV2ScoreFieldKey(key)) continue;
        visit(key, topExplanation[key]);
    }
};

/**
 * Content shown under a section: scored → `explanation` only; plain object → itself; array → null (caller handles).
 */
export const getV2SectionFieldRecord = (
    value: unknown
): { fields: Record<string, unknown>; order?: Array<string | number> | null } | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    if (isScoredNode(value)) {
        return { fields: getScoredNodeExplanation(value), order: value.order };
    }
    const record = value as Record<string, unknown>;
    return {
        fields: record,
        order: Array.isArray(record.order) ? (record.order as Array<string | number>) : undefined,
    };
};

/** True when section has nested non-primitive children (for content border). */
export const hasNestedScoredChildren = (node: V2TopLevelSectionValue): boolean => {
    if (Array.isArray(node)) return node.some((item) => isV2NonPrimitiveValue(item));
    const content = getV2SectionFieldRecord(node);
    if (!content) return false;
    const keys = resolveOrderedKeys(content.fields, content.order);
    return keys.some((k) => !V2_IGNORED_NODE_KEYS.has(k) && isV2NonPrimitiveValue(content.fields[k]));
};

/** Default expansion: open Requests + first nested request accordion. */
export const getDefaultV2ExpansionKeys = (
    payload: ActionOutputV2Payload
): { mainKey: string | null; nestedKeys: string[] } => {
    const entries = buildV2TopLevelAccordionEntries(payload.explanation ?? {}, payload.order);
    const requestsEntry =
        entries.find((e) => e.sectionKey === V2_REQUESTS_SECTION_KEY) ?? entries[0];
    if (!requestsEntry) return { mainKey: null, nestedKeys: [] };

    const nestedKeys: string[] = [];
    const sectionValue = requestsEntry.sectionValue;
    if (isScoredNode(sectionValue)) {
        const explanation = getScoredNodeExplanation(sectionValue);
        if (hasOnlyNumericExplanationKeys(explanation)) {
            const slots = expandNumericKeySlots(
                explanation,
                sectionValue.order,
                V2_REQUESTS_SECTION_KEY
            );
            const first = slots[0];
            if (first) {
                nestedKeys.push(`${requestsEntry.itemKey}-request-${first.index}`);
            }
        }
    }

    return { mainKey: requestsEntry.itemKey, nestedKeys };
};

export const getFieldFormat = (key: string): string | null => {
    const idx = key.indexOf('@');
    return idx >= 0 ? key.slice(idx + 1) : null;
};

export const isV2ScoreFieldKey = (key: string): boolean =>
    key === 'score@percentage' || getFieldFormat(key) === 'score';

export type V2NodeOwnScore = { value: unknown; format: string | null };

export const getV2NodeOwnScore = (node: V2ScoredNode): V2NodeOwnScore => {
    const record = node as Record<string, unknown>;
    if (record.score !== undefined && record.score !== null) {
        return { value: record.score, format: null };
    }
    const suffixedKey = Object.keys(record).find(
        (k) => k.startsWith('score@') && record[k] != null && record[k] !== ''
    );
    return suffixedKey
        ? { value: record[suffixedKey], format: getFieldFormat(suffixedKey) }
        : { value: undefined, format: null };
};

export const getV2NodeDisplayScore = (
    sectionData: V2ScoredNode,
    explanation: Record<string, unknown>
): unknown => {
    const ownScore = getV2NodeOwnScore(sectionData);
    if (ownScore.value !== undefined) {
        return ownScore.value;
    }
    const pct = explanation['score@percentage'];
    if (pct !== undefined && pct !== null && pct !== '') return pct;
    for (const k of Object.keys(explanation)) {
        if (getFieldFormat(k) === 'score' && explanation[k] != null && explanation[k] !== '') {
            return explanation[k];
        }
    }
    return undefined;
};

export const stripFieldSuffix = (key: string): string => {
    const idx = key.indexOf('@');
    return idx >= 0 ? key.slice(0, idx) : key;
};

export const formatSectionTitle = (key: string): string => formatKeyForDisplay(stripFieldSuffix(key));

export const SliceSectionTitle = (key: string): string => {
    const lastDot = key.lastIndexOf('.');
    return lastDot >= 0 ? key.slice(0, lastDot) : key;
};

/** Media-item keys used by media sections, e.g. `oil_leak.mp4__<uuid>` / `1,2_injectors.jpeg__<uuid>`. */
const MEDIA_SOURCE_KEY_RE = /\.[a-z0-9]+__[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export const isMediaSourceKey = (key: string): boolean => MEDIA_SOURCE_KEY_RE.test(key);

const toAttachmentItems = (fieldValue: unknown): unknown[] => {
    if (Array.isArray(fieldValue)) return fieldValue;
    if (fieldValue != null) return [fieldValue];
    return [];
};

/** True when record contains a non-empty `@attachment` field (e.g. `image@attachment`). */
const hasV2AttachmentField = (record: Record<string, unknown>): boolean =>
    Object.entries(record).some(([fieldKey, fieldValue]) => {
        if (getFieldFormat(fieldKey) !== 'attachment') return false;
        return toAttachmentItems(fieldValue).some(
            (item) =>
                item != null &&
                typeof item === 'object' &&
                !Array.isArray(item) &&
                typeof (item as Record<string, unknown>).source_id === 'string' &&
                String((item as Record<string, unknown>).source_id).length > 0
        );
    });

/**
 * Media-item object keyed like `file.jpg__<uuid>` (optionally `@collapse`) whose content
 * includes an attachment field in `explanation` (scored) or as a direct child.
 */
export const isMediaAttachmentObject = (key: string, value: unknown): boolean => {
    if (!isMediaSourceKey(stripFieldSuffix(key))) return false;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const content = getV2SectionFieldRecord(value);
    if (!content) return false;
    return hasV2AttachmentField(content.fields);
};

export const resolveNodeTitle = (key: string): string =>
    isMediaSourceKey(key) ? SliceSectionTitle(key) : formatSectionTitle(key);

const sectionKeyIncludes = (sectionKey: string, fragment: string): boolean => {
    const f = fragment.toLowerCase();
    const keyLower = sectionKey.toLowerCase();
    const baseLower = stripFieldSuffix(sectionKey).toLowerCase();
    return keyLower.includes(f) || baseLower.includes(f);
};

/**
 * Icon for box accordions. When `parentSectionKey` is set (e.g. video_analysis) and `sectionKey`
 * is numeric/UUID, or parent is image_analysis/video_analysis, uses the parent icon + subtitle.
 */
export const pickBoxAccordionIcon = (sectionKey: string, parentSectionKey?: string): BoxAccordionIcon => {
    const useParentIcon = parentSectionKey && (isNumericExplanationKey(sectionKey) || parentSectionKey === 'image_analysis' || parentSectionKey === 'video_analysis');
    const lookupKey = useParentIcon ? parentSectionKey : sectionKey;

    const known = V2_KNOWN_ACTION_BOX[lookupKey];
    if (known) return known;

    for (const { fragment, icon } of V2_KEYWORD_BOX_ICON_RULES) {
        if (sectionKeyIncludes(lookupKey, fragment)) return icon;
    }

    return BOX_ICON_POOL_FALLBACK[hashString(lookupKey) % BOX_ICON_POOL_FALLBACK.length];
};

export const formatCurrencyV2 = (value: number): string => {
    if (!Number.isFinite(value)) return '—';
    if (value < 0) {
        return `-${formatCurrency(Math.abs(value))}`;
    }
    return formatCurrency(value);
};

export const isNumericFieldValue = (value: unknown, requireNonEmptyString = false): boolean => {
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value !== 'string') return false;
    if (requireNonEmptyString && value.trim() === '') return false;
    return !Number.isNaN(Number(value));
};

export const collectSearchableText = (data: unknown): string => {
    if (data == null) return '';
    if (typeof data === 'string') return data.toLowerCase();
    if (typeof data === 'number' || typeof data === 'boolean') return String(data).toLowerCase();
    if (Array.isArray(data)) return data.map(collectSearchableText).join(' ');
    if (typeof data === 'object') {
        return Object.entries(data as Record<string, unknown>)
            .filter(([k]) => !V2_IGNORED_NODE_KEYS.has(k))
            .map(([k, v]) => `${k.toLowerCase()} ${collectSearchableText(v)}`)
            .join(' ');
    }
    return '';
};

export const textMatchesSearch = (text: string, query: string): boolean => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return true;
    const lower = text.toLowerCase();
    return terms.every((term) => lower.includes(term));
};

export const getScoredNodeExplanation = (node: V2ScoredNode): Record<string, unknown> => {
    const exp = node.explanation;
    if (!exp || typeof exp !== 'object' || Array.isArray(exp)) return {};
    return exp;
};

export const getV2StopGates = (payload: ActionOutputV2Payload | null | undefined): string[] => {
    const gates = payload?.stop_gates;
    if (!Array.isArray(gates)) return [];
    return gates.filter((g): g is string => typeof g === 'string' && g.trim().length > 0);
};

export const parseV2StopGatePath = (gate: string): string[] =>
    gate.split('@').map((s) => s.trim()).filter(Boolean);

export type V2StopGateInfo = {
    label: string;
    score: unknown;
    topItemKey: string | null;
    nestedExpandKeys: string[];
    scrollTargetId: string | null;
};

export const segmentMatchesKey = (segment: string, key: string): boolean =>
    key === segment || key.startsWith(`${segment}@`);

const findExplanationKeyForSegment = (keys: string[], segment: string): string | undefined =>
    keys.find((k) => segmentMatchesKey(segment, k));

/** Non-primitive child with no dedicated @format (except @collapse) renders as an accordion. */
export const isTopLevelBodyAccordionChild = (key: string, value: unknown): boolean => {
    const format = getFieldFormat(key);
    if (format != null && format !== 'collapse') return false;
    return isV2NonPrimitiveValue(value);
};

/** Mixed sections render nested objects as line accordions (`…-line`), not box accordions (`…-box-`). */
const sectionRendersLineAccordions = (fields: Record<string, unknown>): boolean =>
    Object.keys(fields).some(
        (key) =>
            !V2_IGNORED_NODE_KEYS.has(key) &&
            key !== 'score' &&
            !isTopLevelBodyAccordionChild(key, fields[key])
    );

/** Open each nested @collapse or media accordion that still has stop-gate path inside it. */
const collectNestedStopGateExpansions = (
    info: V2StopGateInfo,
    node: unknown,
    keyPrefix: string,
    segments: string[]
) => {
    let current: unknown = node;
    let prefix = keyPrefix;
    let rest = segments;
    while (rest.length > 0 && current != null) {
        const content = getV2SectionFieldRecord(current);
        if (!content) return;
        const matchedKey = findExplanationKeyForSegment(Object.keys(content.fields), rest[0]);
        if (!matchedKey) return;
        const child = content.fields[matchedKey];
        rest = rest.slice(1);
        if (isScoredNode(child)) info.score = child.score;

        const isMedia = isMediaAttachmentObject(matchedKey, child);
        const itemKey = isMedia ? `${prefix}-box-${matchedKey}` : `${prefix}-${matchedKey}`;
        if ((getFieldFormat(matchedKey) === 'collapse' || isMedia) && rest.length > 0) {
            info.nestedExpandKeys.push(itemKey);
        }
        prefix = itemKey;
        current = child;
    }
};

export const resolveV2StopGateInfo = (
    payload: ActionOutputV2Payload | null | undefined,
    gate: string
): V2StopGateInfo => {
    const pathSegments = parseV2StopGatePath(gate);
    const label = formatSectionTitle(pathSegments[pathSegments.length - 1] ?? gate);
    const info: V2StopGateInfo = {
        label,
        score: undefined,
        topItemKey: null,
        nestedExpandKeys: [],
        scrollTargetId: null,
    };

    const topExplanation = payload?.explanation;
    if (!topExplanation || typeof topExplanation !== 'object' || pathSegments.length === 0) return info;

    const topKey = findExplanationKeyForSegment(Object.keys(topExplanation), pathSegments[0]);
    if (!topKey) return info;

    const topItemKey = `v2-top-${topKey}`;
    info.topItemKey = topItemKey;
    info.scrollTargetId = topItemKey;

    let currentNode: unknown = (topExplanation as Record<string, unknown>)[topKey];
    if (isScoredNode(currentNode)) info.score = currentNode.score;

    let remaining = pathSegments.slice(1);
    if (remaining.length === 0) return info;

    let boxParentKey = topItemKey;

    if (topKey === V2_REQUESTS_SECTION_KEY && isScoredNode(currentNode)) {
        const requestsExplanation = getScoredNodeExplanation(currentNode);
        if (hasOnlyNumericExplanationKeys(requestsExplanation)) {
            const slots = expandNumericKeySlots(requestsExplanation, currentNode.order, V2_REQUESTS_SECTION_KEY);
            const slot = slots.find((s) => s.key === remaining[0]);
            if (!slot) return info;
            const requestItemKey = `${topItemKey}-request-${slot.index}`;
            boxParentKey = requestItemKey;
            info.scrollTargetId = requestItemKey;
            currentNode = requestsExplanation[slot.key];
            if (isScoredNode(currentNode)) info.score = currentNode.score;
            remaining = remaining.slice(1);
            if (remaining.length > 0) info.nestedExpandKeys.push(requestItemKey);
        }
    }

    if (remaining.length === 0) return info;

    const content = getV2SectionFieldRecord(currentNode);
    if (!content) return info;
    const boxKey = findExplanationKeyForSegment(Object.keys(content.fields), remaining[0]);
    if (!boxKey) return info;

    const boxItemKey = `${boxParentKey}-box-${boxKey}`;
    info.scrollTargetId = boxItemKey;
    const boxNode = content.fields[boxKey];
    if (isScoredNode(boxNode)) info.score = boxNode.score;
    remaining = remaining.slice(1);

    if (remaining.length === 0) return info;

    const expandKey = sectionRendersLineAccordions(content.fields)
        ? `${boxParentKey}-${boxKey}-line`
        : boxItemKey;
    info.nestedExpandKeys.push(expandKey);
    info.scrollTargetId = `${boxItemKey}-${remaining.join('-')}`;
    collectNestedStopGateExpansions(info, boxNode, expandKey, remaining);

    return info;
};
